// Singurul loc din UI care știe cum se vorbește cu serverul.
//
// Căile sunt RELATIVE (`/api/...`), niciodată absolute: nginx servește
// staticul și face proxy pe `/api` către control plane, deci UI-ul și API-ul
// sunt pe același origin. Așa cookie-ul de sesiune merge fără CORS și fără
// configurare diferită între local, VM și producție.

const fetchRaw = (method: string, path: string, body?: unknown) =>
  fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

import { arata as arataToast } from "./toasts";
import { t } from "./i18n";
import type { PlansaImport, SolutieRand } from "./nav/api";
// Tipul hârtiei de calc are UN SINGUR STĂPÂN, modulul care-l folosește;
// import de tip, șters la compilare, deci fără legătură la rulare.
import type { Calc } from "./viewer/tronson/calc";
import type { Avertisment } from "./viewer/tronson/editor/avertismente";

export class ApiError extends Error {
  /** Corpul răspunsului, când serverul a trimis unul.
   *
   *  Codul și mesajul ajung pentru aproape tot, dar unele refuzuri poartă ce
   *  trebuie ARĂTAT: un 403 „no_access" vine cu numele proiectului și cu
   *  oamenii care pot da accesul, iar fără ei ecranul ar spune «cere-i
   *  administratorului», ceea ce într-o firmă de treizeci de oameni nu e o
   *  instrucțiune. */
  constructor(readonly status: number, readonly code: string, message?: string,
              readonly date?: Record<string, unknown>) {
    super(message || code);
  }
}

export type OptiuniCerere = {
  /** o citire de FUNDAL (sondaj la câteva secunde): eșecul ei nu ajunge toast.
   *  Sondajul o va cere din nou la următorul tic, iar un 503 de la un serviciu
   *  ocupat cu calculul nu e o veste pentru om — pe ecranul Soluții, un lanț
   *  în lucru făcea să curgă «Ceva n-a mers (503)» pentru pași pe care ecranul
   *  nici nu-i arată (Stefan, 04.09.2026). */
  silent?: boolean;
};

async function request<T>(method: string, path: string, body?: unknown,
                          opt: OptiuniCerere = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetchRaw(method, path, body);
  } catch (err) {
    // Rețeaua căzută sau serverul mort nu produc un răspuns deloc. Fără mesaj,
    // ecranul pare pur și simplu înghețat.
    if (!opt.silent) {
      arataToast(t("api.server_mut"));
    }
    throw err;
  }

  if (!response.ok) {
    // Serverul răspunde cu {error: "cod"}; când nu poate (502 de la nginx,
    // de exemplu), rămânem cu textul de stare.
    const payload = await response.json().catch(() => null);
    // Doar 5xx ajunge toast. Un 401, un 404 sau un 409 sunt răspunsuri
    // AȘTEPTATE, pe care ecranul le traduce în ceva util („email sau parolă
    // greșite"); transformate în „ceva n-a mers" ar fi mai puțină informație,
    // nu mai multă. Un 5xx e altceva: nimeni nu-l aștepta, și dacă nu se vede,
    // ecranul pare doar că nu reacționează.
    if (response.status >= 500 && !opt.silent) {
      arataToast(t("api.esec", { stare: response.status, metoda: method, cale: path }));
    }
    throw new ApiError(
      response.status,
      payload?.error ?? "unknown",
      payload?.message ?? response.statusText,
      payload ?? undefined,
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const get = <T,>(path: string, opt?: OptiuniCerere) =>
  request<T>("GET", path, undefined, opt);
export const post = <T,>(path: string, body?: unknown, opt?: OptiuniCerere) =>
  request<T>("POST", path, body, opt);
export const put = <T,>(path: string, body?: unknown) => request<T>("PUT", path, body);
export const patch = <T,>(path: string, body?: unknown) => request<T>("PATCH", path, body);
export const del = <T,>(path: string) => request<T>("DELETE", path);

// --- tipuri ----------------------------------------------------------------

export type OrgRole = "owner" | "admin" | "member";

export type Membership = {
  id: string;
  name: string;
  slug: string;
  role: OrgRole;
};

export type CurrentUser = {
  id: string;
  email: string;
  full_name: string | null;
  /** limba interfeței, cod ISO 639-1 — v. `i18n/limbi.ts` și `core.app_user` */
  language: string;
  organizations: Membership[];
};

/** Conturul nivelurilor supraterane, normalizat într-un pătrat unitate. Un
 *  CORP e o structură a amplasamentului (un ansamblu are mai multe, în același
 *  sistem de coordonate), iar un BLOC e un teanc de niveluri cu același contur
 *  — un etaj tip repetat e un contur cu un contor, nu cincisprezece contururi.
 *  Scris de `services/silueta.py` din documentul canonic al soluției de
 *  arhitectură; desenat de `components/Volum`. */
export type Silueta = {
  v: number;
  niveluri: number;
  corpuri: { blocuri: SiluetaBloc[] }[];
};

export type SiluetaBloc = {
  poly: [number, number][];
  niveluri: number;
  /** înălțimea unui nivel, în aceleași unități ca `poly` */
  h: number;
  /** cota la care începe blocul, măsurată de la primul nivel suprateran */
  z: number;
};

/** Cele trei trepte de acces la un proiect. `viewer` citește, `editor`
 *  lucrează (urcă acte, pornește rulări, promovează), `admin` face tot asta
 *  plus împarte accesul, redenumește și arhivează. */
export type ProjectRole = "viewer" | "editor" | "admin";

/** Ce fel de proiect e: `complet` pornește din actele terenului și primește
 *  toate disciplinele, `structura` pornește dintr-o arhitectură existentă și
 *  primește structura și devizul ei. De el atârnă ce cere dosarul și ce pași
 *  urmează. */
export type TipProiect = "complet" | "structura";

/** Cum pornește structura: geometria macro — pozițiile și secțiunile
 *  elementelor verticale și pozițiile grinzilor — e `generata` de platformă pe
 *  arhitectură sau `importata` din planșele care o au deja desenată. Hotărăște
 *  ce Soluție 1 pornește singură după import; din lista de soluții se pot
 *  porni oricând amândouă (`nav/actiuni.ts`). */
export type GeometrieMacro = "generata" | "importata";

export type Project = {
  id: string;
  name: string;
  tip: TipProiect;
  geometrie_macro: GeometrieMacro;
  organization: { id: string; name: string };
  /** Rolul CELUI CARE ÎNTREABĂ pe proiectul ăsta — după el se ascund în
   *  interfață butoanele pe care serverul le-ar refuza oricum. */
  role: ProjectRole | null;
  created_at: string | null;
  updated_at: string | null;
  archived_at: string | null;
  /** Ce se poate spune despre proiect FĂRĂ să-l deschizi — cardul din lista de
   *  proiecte. Vine doar de la listare (`GET /api/projects`); citirea unui
   *  singur proiect n-are nevoie de el și nu-l calculează. */
  rezumat?: ProjectRezumat;
};

export type ProjectRezumat = {
  /** localitatea din dosar, nu adresa întreagă */
  adresa: string | null;
  regim: string | null;
  /** niveluri SUPRATERANE — atât desenează miniatura cardului */
  niveluri: number | null;
  teren_mp: number | null;
  fisiere: number;
  discipline: { key: string; versiune: number }[];
  /** verificările picate ale ultimei soluții de structură; `null` = n-are una */
  neconforme: number | null;
  /** conturul nivelurilor, din soluția de arhitectură — clădirea, nu regimul */
  silueta: Silueta | null;
  job: { kind: string; status: string; eticheta: string } | null;
};

export type Member = {
  user_id: string;
  email: string;
  full_name: string | null;
  /** CUM SE SCRIE OMUL PE ECRAN — aceeași regulă peste tot, calculată de server
   *  (`services/oameni.py`). `full_name` și `email` rămân pentru dezambiguizare;
   *  ce se AFIȘEAZĂ e ăsta, ca să nu mai iasă «Bianca» pe un cartonaș și
   *  «olivia@optimusbuild.ai» pe cel de lângă. */
  nume?: string | null;
  role: OrgRole;
  disabled: boolean;
};

export type Invitation = {
  id: string;
  email: string;
  role: OrgRole;
  created_at: string | null;
  expires_at: string;
  accepted_at: string | null;
  // Prezent DOAR în răspunsul la creare: baza ține numai hash-ul tokenului,
  // deci linkul nu mai poate fi recuperat după aceea.
  link?: string;
};

export type InvitationPreview = {
  email: string;
  role: OrgRole;
  organization: { id: string; name: string };
};

// --- accesul la proiect ------------------------------------------------------

export type AccessUser = {
  id: string;
  email: string;
  full_name: string | null;
  disabled: boolean;
};

/** Un grant dat unui OM. Cel dat organizației întregi are altă formă (n-are
 *  `user`) și stă separat în răspuns, fiindcă e un rând de alt fel în pagină. */
export type UserGrant = {
  id: string;
  role: ProjectRole;
  created_at: string | null;
  user: AccessUser;
};

export type ProjectAccess = {
  /** rolul celui care a cerut pagina */
  role: ProjectRole | null;
  can_manage: boolean;
  organization: { id: string; name: string };
  /** „toți din organizație", sau `null` dacă proiectul nu e dat pe organizație */
  org_grant: { id: string; role: ProjectRole; created_at: string | null } | null;
  users: UserGrant[];
  /** Cine are acces FĂRĂ grant: owner-ii și adminii organizației. Nu se pot
   *  scoate de aici — accesul lor vine din rolul din organizație. */
  implicit: { user: AccessUser; org_role: OrgRole }[];
};

export type GrantBody =
  | { subject: "organization"; role: ProjectRole }
  | { subject: "user"; user_id: string; role: ProjectRole };

// --- apeluri ---------------------------------------------------------------

export const login = (email: string, password: string) =>
  post<CurrentUser>("/api/auth/login", { email, password });

export const logout = () => post<{ ok: boolean }>("/api/auth/logout");

/** `silent`: la deschiderea aplicației cererea se reîncearcă o dată (v.
 *  `session.tsx`), iar un toast «serverul e mut» la prima încercare ar fi
 *  vorbit despre o cădere pe care a doua o repară. */
export const me = (opt?: OptiuniCerere) =>
  get<CurrentUser>("/api/auth/me", opt);

export const previewInvitation = (token: string) =>
  get<InvitationPreview>(`/api/auth/invitation/${encodeURIComponent(token)}`);

export const acceptInvitation = (token: string, password: string, fullName: string) =>
  post<CurrentUser>(`/api/auth/invitation/${encodeURIComponent(token)}/accept`, {
    password,
    full_name: fullName,
  });

export const listProjects = (orgId?: string) =>
  get<Project[]>(orgId ? `/api/projects?org=${encodeURIComponent(orgId)}` : "/api/projects");

export const createProject = (orgId: string, name: string, tip: TipProiect,
                              geometrieMacro?: GeometrieMacro) =>
  post<Project>("/api/projects", { org_id: orgId, name, tip,
                                   ...(geometrieMacro ? { geometrie_macro: geometrieMacro } : {}) });

export const setGeometrieMacro = (id: string, geometrieMacro: GeometrieMacro) =>
  patch<Project>(`/api/projects/${id}`, { geometrie_macro: geometrieMacro });

export const archiveProject = (id: string) => del<Project>(`/api/projects/${id}`);

export const renameProject = (id: string, name: string) =>
  patch<Project>(`/api/projects/${id}`, { name });

/** Cine are acces la proiect. Toate cele trei apeluri întorc pagina ÎNTREAGĂ,
 *  nu doar rândul atins: o schimbare de acces poate muta și alte rânduri (rolul
 *  tău, ce butoane ai voie să apeși), iar un `PUT` care ar întoarce doar
 *  grantul ar cere oricum un `GET` după el. */
export const getAccess = (projectId: string) =>
  get<ProjectAccess>(`/api/projects/${projectId}/access`);

export const grantAccess = (projectId: string, body: GrantBody) =>
  put<ProjectAccess>(`/api/projects/${projectId}/access`, body);

export const revokeAccess = (projectId: string, grantId: string) =>
  del<ProjectAccess>(`/api/projects/${projectId}/access/${grantId}`);

export const listMembers = (orgId: string) =>
  get<Member[]>(`/api/orgs/${orgId}/members`);

export const listInvitations = (orgId: string) =>
  get<Invitation[]>(`/api/orgs/${orgId}/invitations`);

export const createInvitation = (orgId: string, email: string, role: OrgRole) =>
  post<Invitation>(`/api/orgs/${orgId}/invitations`, { email, role });

export const deleteInvitation = (orgId: string, invitationId: string) =>
  del<{ ok: boolean }>(`/api/orgs/${orgId}/invitations/${invitationId}`);

export const disableMember = (orgId: string, userId: string) =>
  post<Member>(`/api/orgs/${orgId}/members/${userId}/disable`);

export const enableMember = (orgId: string, userId: string) =>
  post<Member>(`/api/orgs/${orgId}/members/${userId}/enable`);

/** Profilul propriu. Câmpurile lipsă nu se ating — serverul scrie doar ce
 *  primește —, deci schimbarea limbii nu trece și numele prin `trim`. */
export const updateProfile = (schimbari: { full_name?: string; language?: string }) =>
  patch<CurrentUser>("/api/auth/me", schimbari);

export const changePassword = (currentPassword: string, newPassword: string) =>
  post<{ ok: boolean }>("/api/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });

// --- dosar ------------------------------------------------------------------

export type PiesaFisier = {
  dosar: string;
  pagini: [number, number];
  motiv?: string | null;
  stare?: string | null;
};

export type ProjectFile = {
  id: string;
  name: string;
  folder: string;
  size: number;
  /** piesa de DOSAR: din ce act face parte (certificat de urbanism, cadastru…) */
  dosar: string | null;
  dosar_sursa: string | null;
  /** ce s-a întâmplat cu actul la ultima citire: citit | sarit | eroare | necitit */
  citire_stare: string | null;
  /** de ce — motivul săririi sau mesajul erorii */
  citire_motiv: string | null;
  /** piesele unui fișier cu MAI MULTE acte în el (`dosar === "mixt"`): fiecare
   *  cu categoria, paginile ei (1-based, inclusiv) și ce s-a întâmplat la
   *  citirea ei; lipsesc pe fișierul de un singur fel */
  piese?: PiesaFisier[] | null;
  /** ce PLANȘĂ e desenul — alt lucru decât `dosar`, și chiar diverg */
  categorie: string | null;
  nivel: number | null;
  corp: string | null;
  motiv: string | null;
  sursa: string | null;
  zone: unknown[];
  created_at: string | null;
  /** documentul citit ca OFERTĂ pe Planificare → Oferte: starea citirii,
   *  scenariul făcut din ea și soluția a cărei listă de cantități a prețuit-o
   *  (`control-plane/api/dossier.py`); `null` pe celelalte */
  oferta?: { id: string; stare: string; scenariu_id: string | null; scenariu: string | null;
             solution_id: string | null } | null;
};

export type Category = { cheie: string; eticheta: string; se_extrage: boolean };

export type ProjectData = {
  data: Record<string, unknown>;
  provenance: Record<string, string>;
  manual: string[];
  updated_at: string | null;
};

/** Parametrii unui text codat de job: ce umple locurile goale ale frazei. */
export type ParametriCod = Record<string, string | number | null>;

/** Motivul unei așteptări, cum îl scrie jobul: `motiv_cod` (+ `motiv_param`)
 *  e ce se traduce; `motiv` e același text în română, pentru jurnal și pentru
 *  rândurile scrise înainte de cod. */
export interface MotivAsteptare {
  motiv?: string | null;
  motiv_cod?: string | null;
  motiv_param?: ParametriCod | null;
}

/** Titlul pasului în lucru al unui job (`job_step`), pe același drum:
 *  `titlu_cod` (+ `titlu_param`) se traduce, `titlu` e textul românesc. */
export interface TitluPas {
  titlu: string | null;
  titlu_cod?: string | null;
  titlu_param?: ParametriCod | null;
}

/** Etapa unui job, pe același drum: `stage_cod` (+ `stage_param`) se
 *  traduce, `stage` e textul românesc al jobului (v. `etapaJobului`). */
export interface EtapaJob {
  stage: string | null;
  stage_cod?: string | null;
  stage_param?: ParametriCod | null;
}

export type Job = {
  id: string;
  kind: string;
  service: string;
  /** `waiting`: pasul s-a terminat, următorul așteaptă alt job (v. `asteapta`).
   *  Nu e un proces viu — nimeni nu-i bate pulsul — și nu e nici încheiat. */
  status: "pending" | "running" | "waiting" | "succeeded" | "failed" | "cancelled";
  stage: string | null;
  stage_cod?: string | null;
  stage_param?: ParametriCod | null;
  step_index: number | null;
  step_total: number | null;
  output: Record<string, unknown> | null;
  error: { code?: string; message?: string } | null;
  /** Ce așteaptă un job în `waiting`: felul jobului așteptat, amontele lui,
   *  jobul (odată pornit) și motivul — codul lui, după care se scrie fraza
   *  din catalog, și textul românesc al jobului (v. `motivAsteptarii`). */
  asteapta?: { kind: string; upstream_solution_id?: string | null;
               job_id?: string | null } & MotivAsteptare | null;
  /** Pasul cerut la ultima invocare a serviciului: 2 pe un job reluat. */
  step?: number | null;
  /** Ce a completat platforma pentru că datele nu spuneau — se arată omului. */
  presupuneri?: string[];
  /** Soluția din amonte pe care s-a cerut rularea, când felul ei are una. */
  upstream_solution_id?: string | null;
  /** Soluția pe care o scrie rularea, dacă o are deja deschisă (structura, da;
   *  arhitectura, abia la final — de aici cardurile fantomă). */
  solution_id?: string | null;
  /** «arhitectura» pe rularea care CERE un lanț după ea, «auto» pe cea pornită
   *  de platformă, absent pe cele apăsate de om. */
  inlantuire?: string | null;
  is_dead: boolean;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
};

export const listFiles = (projectId: string) =>
  get<ProjectFile[]>(`/api/projects/${projectId}/files`);

export const listCategories = (projectId: string) =>
  get<Category[]>(`/api/projects/${projectId}/dossier/categories`);

export type UploadResult = {
  files: ProjectFile[];
  rejected: { name: string; error: string }[];
  // Citirea actelor NOI, pornită de platformă la fiecare încărcare: fără ea,
  // datele consolidate ar rămâne cele de dinaintea actului urcat.
  // `pornita: false` are motivul cu el — de obicei „already_running", adică
  // citirea în curs nu cuprinde ce s-a urcat acum.
  citire?: { pornita: boolean; motiv?: string; job?: Job };
  // Parsarea planșelor, pornită tot de platformă (v. `dossier/drawings`).
  desene?: DrawingPrep;
};

/** Încărcarea, cu PROGRES — de asta e XHR și nu `fetch`.
 *
 * `fetch` nu raportează cât s-a trimis din corp (nu în niciun browser de
 * astăzi), iar o încărcare de 200 MB fără bară arată exact ca o încărcare
 * blocată: singurul semn era butonul care scria «Se încarcă…» minute în șir.
 * `XMLHttpRequest.upload.onprogress` e mecanismul care există, deci e cel
 * folosit. Restul (credențiale de același origin, tratarea erorilor, toastul pe
 * 5xx) rămâne ca la celelalte apeluri.
 */
export function uploadFiles(
  projectId: string,
  files: File[],
  onProgress?: (trimis: number, total: number) => void,
): Promise<UploadResult> {
  // multipart, deci fără `Content-Type` pus de noi: browserul trebuie să
  // adauge singur `boundary`, iar un header scris de mână îl împiedică.
  const body = new FormData();
  for (const file of files) {
    body.append("files", file);
    // `webkitRelativePath` există când omul a tras un FOLDER întreg; structura
    // ajunge ca etichetă, chiar dacă fișierele stau plat în stocare.
    body.append("paths", (file as File & { webkitRelativePath?: string })
      .webkitRelativePath || file.name);
  }
  return new Promise<UploadResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/projects/${projectId}/files`);
    xhr.withCredentials = true;
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        // `lengthComputable` e fals când corpul e trimis în flux (rar, dar se
        // întâmplă): atunci nu există procent, iar bara trebuie să treacă în
        // nedeterminat, nu să arate 0 % la infinit.
        onProgress(e.loaded, e.lengthComputable ? e.total : 0);
      };
    }
    xhr.onload = () => {
      let payload: (UploadResult & { error?: string }) | null = null;
      try {
        payload = JSON.parse(xhr.responseText);
      } catch {
        payload = null;
      }
      if (xhr.status >= 200 && xhr.status < 300 && payload) {
        // Ultimul tic: `onprogress` se oprește când s-au trimis octeții, dar
        // serverul mai clasifică după. Fără linia asta bara rămâne la 99 %.
        onProgress?.(1, 1);
        resolve(payload);
        return;
      }
      if (xhr.status >= 500) {
        arataToast(t("api.esec_incarcare", { stare: xhr.status }));
      }
      reject(new ApiError(xhr.status, payload?.error ?? "upload_failed"));
    };
    xhr.onerror = () => {
      arataToast(t("api.server_mut"));
      reject(new ApiError(0, "network"));
    };
    xhr.send(body);
  });
}

/** Starea parsării planșelor, pe id de fișier.
 *
 * `lipsa` = nimeni n-a încercat încă (nu e o eroare); `gata` = vizualizatorul
 * se deschide instantaneu.
 */
export type DrawingPrep = Record<string, {
  stare: "lipsa" | "asteapta" | "lucreaza" | "gata" | "eroare";
  motiv: string | null;
  secunde: number | null;
}>;

export const listDrawingPrep = (projectId: string) =>
  get<DrawingPrep>(`/api/projects/${projectId}/dossier/drawings`);

/** Pregătește planșele care n-au încă desen parsat. Idempotent — ecranul de
 *  dosar îl cere la deschidere, pentru dosarele urcate înainte de mecanism. */
export const prepareDrawings = (projectId: string) =>
  post<DrawingPrep>(`/api/projects/${projectId}/dossier/drawings`, {});

export const patchFile = (projectId: string, fileId: string,
                         fields: Partial<Record<string, unknown>>) =>
  patch<ProjectFile>(`/api/projects/${projectId}/files/${fileId}`, fields);

/** Reprocesează UN act: se aruncă ce s-a derivat din el și se citește din nou.
 *
 *  Cazul pentru care există: zona de interes s-a schimbat pe o planșă, deci ce
 *  s-a citit PRIN ea (conturul lotului, categoria dedusă din desen) nu mai e
 *  valabil. Ieftin: citirea care pleacă după e `doar_noi`, deci atinge exact
 *  actul ăsta, nu dosarul întreg. */
export const reprocessFile = (projectId: string, fileId: string) =>
  post<{
    ok: boolean;
    fisier: string;
    /** A contribuit actul la datele consolidate? Dacă da, ele se schimbă. */
    contribuia: boolean;
    /** Categoria de planșă retrasă, când fusese dedusă din conținutul desenului. */
    categorie_retrasa: string | null;
    desene?: DrawingPrep;
    citire?: { pornita: boolean; motiv?: string; job?: Job };
  }>(`/api/projects/${projectId}/files/${fileId}/reprocess`);

/** `arhitectura_fara_plansa`: planșa de nivel din care s-a importat arhitectura
 *  ★, dacă fișierul șters era una (`control-plane/api/dossier.py`). */
export const deleteFile = (projectId: string, fileId: string) =>
  del<{ ok: boolean;
        arhitectura_fara_plansa?: (PlansaImport & { solution_id: string }) | null }>(
    `/api/projects/${projectId}/files/${fileId}`);

export const getProjectData = (projectId: string) =>
  get<ProjectData>(`/api/projects/${projectId}/data`);

export const getProject = (projectId: string) =>
  get<Project>(`/api/projects/${projectId}`);

export const listJobs = (projectId: string) =>
  get<Job[]>(`/api/projects/${projectId}/jobs`);

// Optional job inputs: `params` travels verbatim to the worker;
// `upstream_solution_id` names the upstream solution the run builds on.
// Structura pornește pe FAZĂ, prin `nav/api.pornesteStructura` (contractul
// «soluțiile pe faze», §5.1) — de acolo vin `faza`, `solution_id` și
// `parent_solution_id`.
export type JobOptions = {
  params?: Record<string, unknown>;
  upstream_solution_id?: string;
  /** Numai la `external_architecture`: după import vine singură Soluția 1 a
   *  structurii, nu doar schița (butonul «Generează soluția structurală» din
   *  dosar, v. `api/jobs.py`). */
  cautare_structura?: boolean;
  faza?: "schita" | "macro" | "detaliu";
  solution_id?: string;
  parent_solution_id?: string;
  /** Numai la `facade`: MODUL DE EDITARE al agentului — cererea omului din
   *  discuție și, dacă a dat una, poza lui (data URL JPEG, micșorată). */
  cerere?: string;
  poza?: string;
  /** Numai la `facade`, cu `cerere`: REFACEREA — fațada se derivă de la zero
   *  după poză și cerere, nu se editează ce e (v. `fatada/agent.py`). */
  refacere?: boolean;
  /** Numai la `facade`, cu `cerere`: SELECȚIA de pe machetă, ca fișe (v.
   *  `viewer/fatada/selectie.ts`) — elementele și zonele despre care e cererea. */
  tinta?: unknown[];
  /** Numai la `export`: formatul (`pud`) și, dacă omul o alege, scara planșei. */
  format?: string;
  scara?: number;
  /** Numai la `mep`: un strat de profil numai pentru rularea asta, peste
   *  setările proiectului (v. `mep/motor/profil.py`). */
  setari?: SetariInstalatii;
};

// --- instalațiile (v. `mep/`) -----------------------------------------------

/** Un strat peste profilul de instalații al țării: `{tema: {cheie: valoare}}`,
 *  cu numele din `mep/profiluri/` (`apa.varianta`, `ghene.wc_max_m`…). */
export type SetariInstalatii = Record<string, Record<string, unknown>>;
export const setariInstalatii = (projectId: string) =>
  get<{ setari: SetariInstalatii }>(`/api/projects/${projectId}/instalatii/setari`);
export const scrieSetariInstalatii = (projectId: string, setari: SetariInstalatii) =>
  put<{ setari: SetariInstalatii }>(`/api/projects/${projectId}/instalatii/setari`, { setari });

// --- exportul (v. `exporter/`) ---------------------------------------------

/** Un fapt al planșei PUD: valoarea, de unde vine și dacă e știută. */
export type FaptPud = {
  cheie: string;
  valoare: unknown;
  sursa: string | null;
  stare: "cunoscut" | "aproximat" | "lipsa";
  nota: string | null;
  um: string | null;
};
export type FaptePud = { fapte: FaptPud[]; coala: string; scara: number; lipsa: string[] };

/** Fișierele exportului de pe o soluție: adresa de descărcare (sau de
 *  previzualizare, `inline`). Aceeași rută servește PDF-ul, DXF-ul și faptele. */
export const exportUrl = (projectId: string, solutionId: string,
                          fel: "pud_pdf" | "pud_dxf" | "pud_fapte", inline = false) =>
  `/api/projects/${projectId}/solutions/${solutionId}/export/${fel}${inline ? "?inline=1" : ""}`;
export const faptePud = (projectId: string, solutionId: string) =>
  get<FaptePud>(exportUrl(projectId, solutionId, "pud_fapte"), { silent: true });
export const corectiiPud = (projectId: string) =>
  get<{ fapte: Record<string, string | number | boolean> }>(`/api/projects/${projectId}/pud/corectii`);
export const scrieCorectiiPud = (projectId: string, fapte: Record<string, string | number | boolean>) =>
  put<{ fapte: Record<string, string | number | boolean> }>(`/api/projects/${projectId}/pud/corectii`, { fapte });

/** Răspunsul la pornirea unei rulări: jobul cerut, plus cel pornit ALĂTURI de
 *  el. Citirea actelor duce cu ea importul planșelor DWG, fiindcă pentru om
 *  amândouă sunt «procesează dosarul» — v. `api/jobs.py: _importul_planselor`. */
export type JobStart = Job & { alaturi?: Job };

export const startJob = (projectId: string, kind: string, options?: JobOptions) =>
  post<JobStart>(`/api/projects/${projectId}/jobs`, { kind, ...(options ?? {}) });

/** Butonul ■: oprește rularea în curs (contract §5.2).
 *
 *  Un job `pending` sau `waiting` se anulează pe loc; unul `running` se
 *  oprește după trecerea sau pasul în curs. Răspunsul e 202, nu 200: s-a
 *  CERUT oprirea, nu s-a terminat încă; la final rândul devine `oprita`. */
export const stopJob = (projectId: string, jobId: string) =>
  post<{ ok: boolean }>(`/api/projects/${projectId}/jobs/${jobId}/stop`, {});

/** «Oprește toate soluțiile»: tot ce rulează sau așteaptă pe proiect, la orice
 *  disciplină.
 *
 *  Răspunsul e pe TREI cifre, fiindcă atât se poate face onest (v.
 *  `api/jobs.py: stop_all_jobs`): programatele se anulează complet, structurii
 *  în curs i se CERE oprirea, iar un plan de situație sau o arhitectură deja
 *  pornite macină până la capăt — jobul lor se marchează anulat, deci nu se mai
 *  înlănțuie nimic peste el, dar solverul nu are unde asculta cererea.
 *  `nu_se_pot_opri` le numește, ca butonul să nu raporteze „oprit" pentru ce
 *  n-a oprit. */
export type StopAll = {
  ok: boolean;
  anulate: number;
  oprire_ceruta: number;
  nu_se_pot_opri: string[];
};

export const stopAllJobs = (projectId: string) =>
  post<StopAll>(`/api/projects/${projectId}/jobs/stop-all`, {});

/** «Șterge toate soluțiile»: soluțiile proiectului, cu rulările și
 *  artefactele lor (v. `api/solutions.py: delete_all`). `"all"` le ia pe
 *  toate — plan de situație, arhitectură, structură —, `"structure"` numai
 *  structura. Rulările vii primesc întâi cererea de oprire. Numerotarea
 *  cardurilor repornește de la S1. Dosarul, actele citite și preferințele
 *  rămân neatinse: se șterg SOLUȚIILE, nu proiectul. */
export type Sterse = {
  ok: boolean; solutii: number; joburi: number; artefacte: number;
  blob: number; feedback: number; jurnal: number;
};
export const deleteAllSolutions = (
  projectId: string, discipline: "all" | "structure" = "all",
) =>
  del<Sterse>(`/api/projects/${projectId}/solutions?discipline=${discipline}`);

// --- soluții ----------------------------------------------------------------

/** ★ la planul de situație și la arhitectură (contract §5.5). La structură
 *  serverul refuză cu 409 `promovare_nepermisa`: ce se deschide implicit
 *  decide `registru.implicit`. Registrul însuși se citește din `nav/api`. */
export const promoteSolution = (projectId: string, solutionId: string) =>
  post<SolutieRand>(`/api/projects/${projectId}/solutions/${solutionId}/promote`);

/** Documentul canonic al soluției.
 *
 *  `felie` cere doar o parte din el: `{parte: "start"}` întoarce contextul,
 *  planșeele și obiectele de la cota pe care se deschide documentul —
 *  atât desenează primul ecran —, iar `{parte: "rest", nivel}` restul,
 *  disjunct. Răspunsul poartă `felie.complet`, deci clientul nu ghicește dacă
 *  mai e ceva de cerut. `{parte: "macheta"}` e documentul cu toate obiectele,
 *  dar fără diagnosticele pe care nu le citește niciun ecran — ce cere macheta
 *  3D, care are nevoie de toate nivelurile. Fără argument vine documentul
 *  întreg, ca până acum. V. `control-plane/services/felii.py`. */
export const solutionDocument = (
  projectId: string, solutionId: string,
  felie?: { parte: "start" | "rest" | "macheta"; nivel?: number | null },
) => {
  const q = new URLSearchParams();
  if (felie) {
    q.set("parte", felie.parte);
    if (felie.nivel != null) q.set("nivel", String(felie.nivel));
  }
  const qs = q.toString();
  return get<{ objects?: unknown[]; felie?: { parte: string; nivel: number | null;
                                              complet: boolean };
               [key: string]: unknown }>(
    `/api/projects/${projectId}/solutions/${solutionId}/document${qs ? `?${qs}` : ""}`);
};

// --- feed, feedback, verificări și cost pe soluție ---------------------------

/** Un eveniment din feed-ul proiectului („ce s-a întâmplat").
 *
 *  Feed-ul NU e o tabelă: control plane-ul îl compune la citire din joburi,
 *  soluții, cereri de modificări și jurnalul proiectului (`core.event`),
 *  împletite pe timp
 *  (`api/structure.py::feed`). De aici forma: `fel` spune din ce sursă vine
 *  rândul, `clasa` ce culoare are punctul, `la` e ISO 8601, iar cheile în plus
 *  depind de sursă (un rând de soluție poartă `solution_id`, unul de job
 *  `job_id`…). */
export type FeedEvent = {
  fel: "job" | "solutie" | "feedback" | "eveniment" | string;
  clasa: "ok" | "bad" | "info" | "feedback" | string;
  la: string;
  mesaj: string;
  job_id?: string;
  solution_id?: string;
  feedback_id?: string;
  resulting_solution_id?: string | null;
  kind?: string;
  status?: string;
  discipline?: string;
  promoted?: boolean;
  [key: string]: unknown;
};

export const getFeed = (projectId: string) =>
  get<FeedEvent[]>(`/api/projects/${projectId}/feed`);

export type FeedbackRequest = {
  id: string;
  solution_id: string | null;
  body: string;
  /** la ce se referă cererea (selecția din Studio, fațada, nivelurile…) */
  scope: Record<string, unknown> | null;
  created_at: string | null;
  /** soluția NĂSCUTĂ din cerere — o completează generatorul, la final */
  resulting_solution_id?: string | null;
  [key: string]: unknown;
};

export const listFeedback = (projectId: string) =>
  get<FeedbackRequest[]>(`/api/projects/${projectId}/feedback-requests`);

export const createFeedback = (
  projectId: string,
  req: {
    solution_id?: string | null;
    body: string;
    scope?: Record<string, unknown> | null;
  },
) => post<FeedbackRequest>(`/api/projects/${projectId}/feedback-requests`, req);

/** o verificare a unei soluții, în forma normalizată a control plane-ului */
export type SolutionCheck = {
  id?: string;
  title?: string;
  status?: string;
  value?: number | null;
  threshold?: number | null;
  [key: string]: unknown;
};

export const getChecks = (
  projectId: string,
  solutionId: string,
  params?: Record<string, string | number | boolean>,
) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) q.set(k, String(v));
  const qs = q.toString();
  return get<SolutionCheck[]>(
    `/api/projects/${projectId}/solutions/${solutionId}/checks${qs ? `?${qs}` : ""}`,
  );
};

export const getCost = (projectId: string, solutionId: string) =>
  get<Record<string, unknown>>(
    `/api/projects/${projectId}/solutions/${solutionId}/cost`,
  );

/** O încărcare de pagină, în `core.activity_log`.
 *
 * NU trece prin `request`: e o scriere de fundal, iar un eșec al ei n-are ce
 * căuta într-un toast peste ecranul omului — nici măcar la 500. Se trimite
 * DOAR calea; cine e omul și de la ce IP vine le pune serverul, din cookie și
 * din antetele lui nginx.
 *
 * `keepalive` ține cererea în viață dacă browserul pleacă de pe pagină imediat
 * după (un clic pe un link extern, un refresh): altfel exact navigările scurte
 * ar lipsi din jurnal.
 */
export function logPageView(path: string): void {
  fetch("/api/activity", {
    method: "POST",
    credentials: "same-origin",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ path }),
  }).catch(() => undefined);
}

// ---------------------------------------------------------------------------
// Colecția de tronsoane
// ---------------------------------------------------------------------------
//
// Modulele din care se compune o bară. Colecția unui proiect e o VEDERE:
// modulele globale, peste care se suprapun ale lui, pe cod — v.
// `control-plane/api/catalog.py`. Serverul derivă categoria și nucleul, ca
// traducerea din `role` să nu aibă o a doua definiție aici.

/** Vederea derivată a unui modul, cea pe care o citește planul de situație.
 *  Are exact ce scrie pe cartonaș, în 300–500 de octeți — de-aia lista n-are
 *  nevoie de documentul întreg. */
export type TronsonAlpha = {
  id: string;
  label?: string;
  role: string;
  front: { nominal_m: number; min_m: number; max_m: number; rigid?: boolean };
  mix: Record<string, number>;
  flats: number;
  core: boolean;
  core_front_m?: number | null;
  leg_m?: number;
  notch_m?: number;
  variants: { id: string; useful_m2: number;
              area_by_camere: Record<string, number>; default?: boolean }[];
};

/** O etichetă din vocabularul proiectului, cu pe câte module e pusă. */
export type Eticheta = {
  id: string;
  nume: string;
  /** Pe câte module e pusă. «social · 6» și «de încercat · 0» se citesc
   *  altfel: a doua se vede că a rămas în urmă. */
  module: number;
};

export type Tronson = {
  code: string;
  version: number;
  status: "draft" | "published" | "retired";
  /** DE UNDE VINE MODULUL, în vocabularul colecției care l-a adus.
   *
   *  Colecția unui PROIECT spune «global» (modulul comun) sau «proiect»
   *  (rescris acolo). Colecția unui OM spune «al_meu», «impartit» (mi l-a dat
   *  cineva) sau «platforma» (dintre cele desenate la început). Un singur tip
   *  pentru amândouă, fiindcă același cartonaș le desenează pe toate — v.
   *  `pages/Colectie.tsx`, care slujește ambele ecrane. */
  scope: "global" | "proiect" | "al_meu" | "impartit" | "platforma";
  /** Al cui e. Numai din colecția unui om; lipsește pe cea a unui proiect. */
  autor?: string | null;
  role: string;
  /** Derivată de server din `role`: `colt` | `capat` | `mijloc`. */
  category: "colt" | "capat" | "mijloc";
  has_core: boolean;
  band_depth_m: number;
  /** Cuvintele proiectului puse pe modulul ăsta. Vin ODATĂ CU RÂNDUL, nu
   *  dintr-o a doua cerere: altfel ar exista o clipă în care cartonașul e
   *  desenat și spune că modulul n-are niciuna. */
  etichete: string[];
  /** CÂND A FOST EDITAT ULTIMA OARĂ, ISO. O editare scrie o versiune NOUĂ,
   *  deci e `created_at` al versiunii curente. Lipsește pe listele care nu-l
   *  cer de la server (v. `_modul`). */
  editat_la?: string | null;
  alpha: TronsonAlpha;
};

/** Un modul din COLECȚIA UNUI OM (`/api/tronsoane`) — v. migrarea 0045.
 *  Aceeași formă ca `Tronson`; numele e aici ca să se citească intenția la
 *  apel, nu fiindcă serverul ar trimite altceva. */
export type TronsonAlMeu = Tronson;
export type ColectieAMea = ColectieTronsoane;

/** Cu cine e împărțit un modul. */
export type GrantTronson = {
  id: string;
  role: "viewer" | "editor";
  fel: "om" | "organizatie";
  cine: string | null;
  user_id: string | null;
  org_id: string | null;
};

// ---------------------------------------------------------------------------
// COLECȚIA OMULUI — `/api/tronsoane`, fără niciun proiect în cale.
//
// Perechea rutelor de pe proiect de mai jos. Cât timp există și tabul din
// proiect, există amândouă; când el dispare, rămân doar astea.
// ---------------------------------------------------------------------------

export const listTronsoaneleMele = () =>
  get<ColectieAMea>("/api/tronsoane");

/** Raportul conversiei DXF → DWG de pe server (`exporter/plansa/dwg.py`). */
export type RaportDwg = {
  ok: boolean; entitati_dxf: number | null; entitati_dwg: number | null;
  pierdute: number | null; mesaj: string;
};

/** PLANȘA UNUI MODUL CA DWG: desenul (primitive pe layere, v.
 *  `viewer/tronson/export/desen`) pleacă la server, fișierul vine înapoi.
 *  Nu trece prin `request`: răspunsul e un fișier, nu JSON, iar raportul
 *  conversiei vine într-un antet. Eroarea e aceeași `ApiError` ca peste tot. */
export async function descarcaDwg(desen: unknown): Promise<{ blob: Blob; raport: RaportDwg | null }> {
  const r = await fetchRaw("POST", "/api/tronsoane/export/dwg", desen);
  if (!r.ok) {
    const payload = await r.json().catch(() => null);
    throw new ApiError(r.status, payload?.error ?? "unknown",
                       payload?.raport?.mesaj ?? payload?.message ?? r.statusText,
                       payload ?? undefined);
  }
  const antet = r.headers.get("X-OB-DWG-Raport");
  let raport: RaportDwg | null = null;
  try { raport = antet ? (JSON.parse(antet) as RaportDwg) : null; } catch { raport = null; }
  return { blob: await r.blob(), raport };
}

/** `ciorna` opens the work in progress, `version` one given version. */
type DeschidereTronson = { ciorna?: boolean; version?: number };

const interogareTronson = (opt?: DeschidereTronson) => {
  const parti = [
    ...(opt?.ciorna ? ["ciorna=1"] : []),
    ...(opt?.version != null ? [`version=${opt.version}`] : []),
  ];
  return parti.length ? `?${parti.join("&")}` : "";
};

export const getTronsonulMeu = (code: string, opt?: DeschidereTronson) =>
  get<Tronson & { doc: unknown }>(
    `/api/tronsoane/${encodeURIComponent(code)}${interogareTronson(opt)}`);

/** What the catalogue flags in a drawing, for the editor to show when it
 *  opens a module. Writes nothing. */
export const avertismenteTronson = (doc: unknown) =>
  post<{ avertismente: Avertisment[] }>("/api/tronsoane/avertismente", { doc });

export const salveazaTronsonulMeu = (code: string, doc: unknown, note?: string,
                                     basedOn?: number) =>
  put<ScriereTronson>(
    `/api/tronsoane/${encodeURIComponent(code)}`,
    { doc, note, based_on: basedOn });

/** One row of a module's history; no document. */
export type VersiuneTronson = {
  version: number;
  status: Tronson["status"];
  source: "person" | "system";
  autor: string | null;
  creat_la: string | null;
  nota: string | null;
  /** The version this one started from, when it is known. */
  based_on: number | null;
};

export type VersiuniTronson = {
  versions: VersiuneTronson[];
  published: number | null;
};

/** A module's history, on the project's rows when `projectId` is given. */
export const versiuniTronson = (code: string, projectId?: string) =>
  get<VersiuniTronson>(
    `${projectId ? `/api/projects/${projectId}/tronsoane` : "/api/tronsoane"}`
    + `/${encodeURIComponent(code)}/versiuni`);

export const tronsonNouAlMeu = (code: string, doc: unknown, note?: string) =>
  post<ScriereTronson>("/api/tronsoane", { code, doc, note });

export const publicaTronsonulMeu = (code: string, version?: number) =>
  post<ScriereTronson>(
    `/api/tronsoane/${encodeURIComponent(code)}/publica`, { version });

export const stergeTronsonulMeu = (code: string) =>
  del<{ code: string; sters: boolean }>(
    `/api/tronsoane/${encodeURIComponent(code)}`);

/** Vocabularul meu de etichete (migrarea 0047) — cuvintele pe care le pot pune
 *  pe modulele din colecția mea. */
export const listEtichetleMele = () =>
  get<{ etichete: Eticheta[] }>("/api/tronsoane/etichete");

/** Etichetele unui modul, TOATE ODATĂ. Se pot pune și pe un modul primit:
 *  cuvântul e al meu, stă în vocabularul meu, și nu-l vede nimeni altcineva. */
export const puneEticheteleMele = (code: string, etichete: string[]) =>
  put<{ code: string; etichete: string[] }>(
    `/api/tronsoane/${encodeURIComponent(code)}/etichete`, { etichete });

/** Pornește citirea unei planșe în colecția mea. Modulul care iese e al meu. */
export const importDinImagineAlMeu = (
  code: string, imagine: string, rol?: string, label?: string,
  calc?: CalcDeImport,
) => post<ImportImagine>("/api/tronsoane/din-imagine",
                         { code, imagine, rol, label, calc });

export const stareImportAlMeu = (importId: string) =>
  get<ImportImagine>(`/api/tronsoane/din-imagine/${importId}`);

/** Hârtia de calc, pe colecția omului (v. `viewer/tronson/calcStocare.ts`). */
export const iaCalculMeu = (code: string) =>
  get<{ calc: Calc | null }>(
    `/api/tronsoane/${encodeURIComponent(code)}/calc`);

export const salveazaCalculMeu = (code: string, calc: Partial<Calc>) =>
  put<{ code: string }>(
    `/api/tronsoane/${encodeURIComponent(code)}/calc`, calc);

export const scoateCalculMeu = (code: string) =>
  del<{ code: string }>(`/api/tronsoane/${encodeURIComponent(code)}/calc`);

/** Împărțirea. `pot_imparti` e adevărat numai pentru PROPRIETAR: un editor
 *  scrie modulul, nu-l dă mai departe. */
export const iaAccesulTronsonului = (code: string) =>
  get<{ pot_imparti: boolean; granturi: GrantTronson[] }>(
    `/api/tronsoane/${encodeURIComponent(code)}/acces`);

export const daAccesTronson = (
  code: string, catre: { user_id?: string; org_id?: string },
  role: "viewer" | "editor" = "viewer",
) => put<{ code: string; role: string }>(
  `/api/tronsoane/${encodeURIComponent(code)}/acces`, { ...catre, role });

export const scoateAccesTronson = (
  code: string, catre: { user_id?: string; org_id?: string },
) => del<{ code: string }>(
  `/api/tronsoane/${encodeURIComponent(code)}/acces`
  + `?${catre.user_id ? `user_id=${catre.user_id}` : `org_id=${catre.org_id}`}`);

export type ColectieTronsoane = {
  modules: Tronson[];
  /** Câte sunt ale proiectului, nu globale. */
  own: number;
  /** Câte sunt CIORNE — salvate, dar neintrate încă în niciun plan. */
  ciorne: number;
  band_depth_m: number | null;
};

export const listTronsoane = (projectId: string) =>
  get<ColectieTronsoane>(`/api/projects/${projectId}/tronsoane`);

/** Documentul întreg al unui modul — pereți, camere, goluri. Se cere pe COD,
 *  iar serverul rezolvă care versiune a lui e a proiectului.
 *
 *  `ciorna` cere CE EDITEZI, nu ce rulează: ultima versiune a proiectului, fie
 *  ea ciornă. Fără el vine cea publicată — cele două sunt întrebări diferite
 *  despre același cod și se aleg explicit.
 *
 *  ÎL CER AMÂNDOUĂ ECRANELE care arată module, și nu din comoditate: de când
 *  colecția cuprinde și ciornele, cartonașul ei descrie rândul nepublicat, deci
 *  apăsarea pe el trebuie să deschidă chiar rândul ăla. Cine are nevoie de ce
 *  RULEAZĂ sunt motoarele, iar ele nu trec pe-aici. */
export const getTronson = (projectId: string, code: string,
                           opt?: DeschidereTronson) =>
  get<Tronson & { doc: unknown }>(
    `/api/projects/${projectId}/tronsoane/${encodeURIComponent(code)}`
    + interogareTronson(opt));

export type ScriereTronson = {
  code: string;
  version: number;
  status: "draft" | "published";
  scope: "proiect";
  /** Ora la care baza a scris versiunea — ceasul pe care colțul editorului îl
   *  REȚINE după o salvare, ca să fie același pe care îl va citi la
   *  redeschidere (v. `tronsoane._scrie`). Lipsește pe rutele de proiect. */
  editat_la?: string | null;
  /** What the catalogue flags in the written drawing; never a refusal. */
  avertismente?: Avertisment[];
};

/** HÂRTIA DE CALC a unui modul — imaginea de sub desen, sau `null`.
 *
 *  Rută a ei, nu un câmp al documentului: o poză de plan n-are ce căuta în ce
 *  se publică și se compară pe `content_hash`, nici în citirea colecției, care
 *  aduce toate modulele deodată. V. `viewer/tronson/calcStocare.ts`.
 */
export const iaCalculModulului = (projectId: string, code: string) =>
  get<{ calc: Calc | null }>(
    `/api/projects/${projectId}/tronsoane/${encodeURIComponent(code)}/calc`);

/** O scrie. FĂRĂ `imagine`, poza de pe rând rămâne cum e și se schimbă doar
 *  calibrarea — drumul pe care merg mișcarea, rotirea și felia de
 *  transparență, care altfel ar fi cărat megaoctetul la fiecare zvâcnire. */
export const salveazaCalculModulului = (
  projectId: string, code: string, calc: Partial<Calc>,
) => put<{ code: string }>(
  `/api/projects/${projectId}/tronsoane/${encodeURIComponent(code)}/calc`, calc);

export const scoateCalculModulului = (projectId: string, code: string) =>
  del<{ code: string }>(
    `/api/projects/${projectId}/tronsoane/${encodeURIComponent(code)}/calc`);

/** Salvează o CIORNĂ. Nu refuză un document neterminat — motoarele nu citesc
 *  ciorne, deci nimic din ea nu poate ajunge într-un plan. Serverul recalculează
 *  ariile, `useful_m2` și `alpha`; ecranul trimite doar geometria. */
export const salveazaTronson = (projectId: string, code: string, doc: unknown,
                                basedOn?: number) =>
  put<ScriereTronson>(
    `/api/projects/${projectId}/tronsoane/${encodeURIComponent(code)}`,
    { doc, based_on: basedOn });

/** Salvează desenul ca un ȘABLON NOU, sub alt cod, la versiunea 1.
 *
 *  Altceva decât o ciornă: ciorna scrie o versiune nouă a ACELUIAȘI modul, pe
 *  care planurile proiectului o primesc în locul celui global. Asta adaugă un
 *  modul lângă celelalte, fără să-l atingă pe cel de la care s-a pornit.
 *
 *  Aruncă la 409 dacă alt modul poartă deja codul cerut — oricare, chiar și unul
 *  global pe care proiectul nu l-a atins. */
export const salveazaTronsonCaNou = (
  projectId: string, code: string, doc: unknown, din?: string,
) => post<ScriereTronson>(`/api/projects/${projectId}/tronsoane`,
                          { code, doc, din });

/** UN PLAN SCANAT SAU FOTOGRAFIAT -> un modul izolat, desenat.
 *
 *  NU TRECE PRIN ASISTENT. Importul are câteva sute de măsurători mărunte, iar
 *  o buclă de discuție le face pe rând, cu toată discuția recitită la fiecare
 *  tur. Aici: un apel, un răspuns, un modul — codul măsoară (ziduri, camere,
 *  scară), modelul e chemat o singură dată pentru ce nu se poate măsura
 *  (etichetele scrise, ariile, felurile). V. `importer/plan/`.
 *
 *  ȚINE ZECI DE SECUNDE și n-are cum altfel: o citire de plan e un apel de
 *  model pe două imagini. Ecranul arată asta; nu e un buton care se blochează.
 *
 *  Aruncă cu un cod de oprire când planșa nu se poate citi — `prea_mica`,
 *  `fara_camere`, `fara_scara` —, iar `audit` spune ce s-a măsurat până acolo. */
/** UN IMPORT DIN IMAGINE, cum îl vede ecranul: citirea o face pe server un
 *  agent care se uită la planșă cu lupa și rigla (minute, nu secunde), deci
 *  pornirea răspunde imediat, iar starea se citește până ajunge `scris` sau
 *  `esuat`. Citirea care găsește documentul gata e și cea care îl scrie. */
export type ImportImagine = {
  id: string;
  code: string;
  stare: "pornit" | "citire" | "gata" | "scris" | "esuat";
  /** Când a pornit citirea, ISO. Bara se mișcă pe ceasul EI: socotită de când
   *  a văzut-o pagina, sărea înapoi la zero la fiecare reîmprospătare. */
  pornit_la?: string | null;
  etapa?: { etapa?: string; tura?: number; secunde?: number };
  audit?: Record<string, unknown>;
  version?: number;
  error?: string;
  message?: string;
};

/** CÂT DE LUNG POATE FI NUMELE UNUI MODUL. Ținut egal cu regula serverului
 *  (`catalog.LUNGIME_COD`); dacă unul se mișcă fără celălalt, ecranul fie
 *  oprește un nume bun, fie trimite unul pe care serverul îl refuză. */
export const LUNGIME_COD = 60;
export const NUME_MODUL = new RegExp(`^[A-Z0-9][A-Z0-9 _-]{0,${LUNGIME_COD - 1}}$`);

/** NUMELE, ADUS LA FORMA PE CARE O CERE `NUME_MODUL`.
 *
 *  Stă lângă regulă fiindcă e aceeași regulă citită invers, iar scrisă de mână
 *  în fiecare ecran se desincronizează: era în trei locuri (naștere,
 *  redenumire, și butonul care se aprinde).
 *
 *  SE CHEAMĂ LA FOLOSIRE, NU LA TASTARE. Un câmp controlat căruia îi
 *  transformi valoarea la fiecare tastă își duce cursorul la capăt: scrii o
 *  literă în mijlocul numelui și următoarea ajunge la sfârșit — «să poți să
 *  scrie în orice punct fără întrerupere» (26.10.2026). Majusculele se VĂD din
 *  `text-transform`, iar numele se curăță abia când se trimite. */
export const numeCurat = (v: string) =>
  v.trim().replace(/\s+/g, " ").toUpperCase();

/** Alt nume pentru un modul care există deja. Numele e și identitatea lui —
 *  hârtia de calc, etichetele, cine-l mai are —, deci le mută serverul pe
 *  toate odată. */
export const redenumesteTronsonulMeu = (cod: string, nou: string) =>
  patch<{ code: string; din: string }>(
    `/api/tronsoane/${encodeURIComponent(cod)}/nume`, { code: nou });

export const redenumesteTronson = (projectId: string, cod: string, nou: string) =>
  patch<{ code: string; din: string }>(
    `/api/projects/${projectId}/tronsoane/${encodeURIComponent(cod)}/nume`,
    { code: nou });

/** Poza micșorată a planșei, trimisă ODATĂ CU CEREREA.
 *
 *  Hârtia de calc o scria ecranul după ce aștepta importul să se termine. De
 *  la 13.10.2026 importul merge în fundal și fereastra pleacă îndată, deci n-ar
 *  mai fi nimeni care s-o scrie: vine de la început și o pune serverul pe modul
 *  în clipa în care îl scrie. */
export type CalcDeImport = { imagine: string; latime: number; inaltime: number };

export const pornesteImportDinImagine = (
  projectId: string, code: string, imagine: string, rol?: string,
  calc?: CalcDeImport,
) => post<ImportImagine>(
  `/api/projects/${projectId}/tronsoane/din-imagine`, { code, imagine, rol, calc });

export const stareImportDinImagine = (projectId: string, id: string) =>
  get<ImportImagine>(`/api/projects/${projectId}/tronsoane/din-imagine/${id}`);

/** Citirile pornite și nesfârșite ale proiectului — pentru barele de pe pagina
 *  colecției. Cererea ASTA e și cea care duce importul la capăt (serverul scrie
 *  modulul când găsește documentul gata), deci pagina trebuie s-o repete. */
/** Oprește citirea și o șterge — cu tot cu modulul, dacă a apucat să se scrie.
 *  Același gest dă la o parte și rândul unei citiri căzute. */
export const opresteImport = (projectId: string, id: string) =>
  del<{ id: string; code: string }>(
    `/api/projects/${projectId}/tronsoane/din-imagine/${id}`);

export const opresteImportulMeu = (id: string) =>
  del<{ id: string; code: string }>(`/api/tronsoane/din-imagine/${id}`);

/** Trimite din nou planșa la citire, pe același rând (planșa e pe server). */
export const reiaImport = (projectId: string, id: string) =>
  post<ImportImagine>(
    `/api/projects/${projectId}/tronsoane/din-imagine/${id}/reia`, {});

export const reiaImportulMeu = (id: string) =>
  post<ImportImagine>(`/api/tronsoane/din-imagine/${id}/reia`, {});

export const importuriInLucru = (projectId: string) =>
  get<{ imports: ImportImagine[] }>(
    `/api/projects/${projectId}/tronsoane/din-imagine`);

export const importurileMeleInLucru = () =>
  get<{ imports: ImportImagine[] }>("/api/tronsoane/din-imagine");

/** Trece ultima versiune pe `published`. Nu e o poartă: desenul nu se judecă
 *  nicăieri pe calea de scriere (19.09.2026). */
export const publicaTronson = (projectId: string, code: string, version?: number) =>
  post<ScriereTronson>(
    `/api/projects/${projectId}/tronsoane/${encodeURIComponent(code)}/publica`,
    { version });

// --- etichetele ---------------------------------------------------------------
//
// Cuvintele proiectului puse pe module — «social», «studio», «varianta
// clientului» —, ca să se poată grupa colecția după altceva decât categoria
// derivată din `role`. Vocabularul e al proiectului și poate sta gol: o etichetă
// scoasă de pe ultimul modul rămâne în listă, ca să fie ALEASĂ, nu tastată din
// nou. Legătura stă pe COD, deci a eticheta un modul global nu-l ramifică și
// nu-i atinge nici documentul, nici amprenta.

/** Vocabularul proiectului: din ce se poate alege, și pe câte module e pus. */
export const listEtichete = (projectId: string) =>
  get<{ etichete: Eticheta[] }>(`/api/projects/${projectId}/etichete`);

/** Etichetele unui modul, PE TOATE ODATĂ — ce trebuie să rămână, nu un adaos.
 *
 *  Ecranul are lista întreagă sub ochi (chipuri cu un «×» și un câmp de
 *  adăugat), deci trimite mulțimea. Cu «adaugă una» / «scoate una» ar fi fost
 *  două apăsări care se pot încrucișa: cine scoate două etichete repede ar fi
 *  putut primi înapoi una dintre ele.
 *
 *  Numele necunoscute se nasc acum, în proiect. Întoarce ce s-a scris —
 *  curățat, fără duplicate, sortat —, nu ce s-a trimis. */
export const puneEtichetele = (projectId: string, code: string,
                               etichete: string[]) =>
  put<{ code: string; etichete: string[] }>(
    `/api/projects/${projectId}/tronsoane/${encodeURIComponent(code)}/etichete`,
    { etichete });

/** Renunță la modulul propriu: proiectul se întoarce la cel global. */
export const stergeTronson = (projectId: string, code: string) =>
  del<{ code: string; sterse: number; scope: "global" }>(
    `/api/projects/${projectId}/tronsoane/${encodeURIComponent(code)}`);

// --- fațada --------------------------------------------------------------------
//
// Stilul (regulile) fațadei e al PROIECTULUI și intră în fiecare job de
// arhitectură pornit după ce e salvat; fiecare soluție îl copiază în documentul
// ei (`Facade.style`). Previzualizarea nu scrie nimic: cere arhitecturii să
// refacă doar etajele soluției cu regulile date și întoarce obiectele de fațadă.

export type StilFatada = {
  version?: number;
  note?: string;
  layers?: { layer: "thermal" | "render"; material: string; thickness_m: number }[];
  /** SISTEMUL FAȚADEI — al clientului, din temă; îl pune platforma, iar
   *  arhitectura îl păstrează pe al documentului (format 3.6, §24). */
  system?: "etics" | "etics_placari" | "ventilata";
  rules: ReguleFatada[];
};

export type SelectFatada = {
  surface?: "facade" | "plinth" | "recess" | "parapet" | "soffit" | "balcony" | "terrace";
  storeys?: "all" | "ground" | "upper" | "top" | number[] | { from?: number; to?: number };
  runs?: number[];
  sides?: number[];
  faces?: string[];
  region?: { u?: [string | number, string | number]; z?: [string | number, string | number] };
  /** Numai pe o regulă `window`: ce goluri alege, și după lățime. */
  openings?: "windows" | "doors" | "all";
  width?: { min?: number; max?: number };
  /** finish/band/window: numai porțiunile feței cu peretele din materialele
   *  astea (zidăria șablonului, betonul structurii, cortina unui `glazing.full`). */
  material?: ("zidarie" | "beton" | "perete_cortina")[];
};

/** TÂMPLĂRIA unui gol (regula `window`): ce umple golul stă pe `Window`/`Door`
 *  (produsul, pe bucăți), glaful și șpaletul sunt panouri de fațadă legate de
 *  gol. Toate cheile sunt opționale: se pun peste implicit, ultima câștigă. */
export type TamplarieFatada = {
  /** fața văzută, adâncimea tocului și cât stă în spatele feței finisajului */
  frame?: { material?: string; profile_m?: number; depth_m?: number; setback_m?: number };
  /** cerceveaua canatului mobil, bagheta celui fix, traversa de jos a foii de ușă */
  sash?: { width_m?: number; step_m?: number; bead_m?: number; door_rail_m?: number };
  mullion_m?: number;
  glazing?: "single" | "double" | "triple";
  leaves?: number;
  leaf_max_m?: number;
  opening?: "fixed" | "casement" | "tilt_turn" | "sliding";
  /** ce face FIECARE canat, de la stânga, repetat pe canate; null = toate ca `opening` */
  modes?: ("fixed" | "casement" | "tilt_turn" | "sliding")[] | null;
  colour?: string;
  /** mânerul de pe fiecare canat mobil; null = fără */
  hardware?: { type?: "lever" | "bar" | "none"; colour?: string; height_m?: number } | null;
  /** pragul ușii; null = fără */
  threshold?: { material?: string; height_m?: number } | null;
  sill?: { material?: string; depth_m?: number; height_m?: number; overhang_m?: number;
           colour?: string } | null;
  reveal?: { material?: string; depth_m?: number; finish?: string } | null;
  /** Rama în jurul golului: patru piese cu volum (ține loc de glaf). */
  surround?: { width_m?: number; depth_m?: number; material?: string; colour?: string } | null;
};

export type ReguleFatada = {
  id?: string;
  note?: string;
  select?: SelectFatada;
  finish?: { kind: "plaster" | "cladding" | "brick" | "glazing"; material: string; colour?: string;
             thickness_m?: number };
  band?: { at?: string | number; height_m?: number; depth_m?: number; material?: string;
           colour?: string; section?: string | number[][];
           /** Interval vertical explicit (două ancore z), în locul lui at + height_m. */
           z?: [string | number, string | number] };
  railing?: { kind: "glass" | "slats" | "solid"; height_m?: number; material?: string;
              colour?: string;
              /** MÂNA CURENTĂ (format 3.9): `false` = fără, altfel ce se schimbă
               *  față de bara implicită */
              handrail?: false | { profile?: "rect" | "round"; width_m?: number; height_m?: number;
                                   material?: string; colour?: string } };
  window?: TamplarieFatada;
  /** PARDOSEALA unui balcon, a unei logii sau a unei terase (format 3.8):
   *  gresie lipită, dale pe ploturi sau deck (numai pe terase). */
  floor?: PardosealaFatada;
  /** PLĂCI ÎN ALTĂ CULOARE (13.10.2026): plăcile calepinajului al căror
   *  mijloc cade în `select.region` iau culoarea asta; restul rămâne. */
  tile?: { colour: string };
};

export type PardosealaFatada = {
  kind: "tiles" | "raised" | "deck";
  material?: string;
  colour?: string;
  /** [lungime, lățime] în metri — la deck, scândura */
  format_m?: [number, number];
  joint_m?: number;
  joint_colour?: string;
  pattern?: "grid" | "stretcher";
  direction?: "along" | "across";
};

/** Un rând din LISTA DE PLĂCI a unui corp (`architecture.fatada.placi`):
 *  material, culoare și format (placa de stoc), cu bucățile, aria și
 *  dimensiunile reale ale plăcilor debitate. */
export type RandPlaci = {
  material: string;
  colour: string | null;
  module_m: [number, number];
  count: number;
  area_m2: number;
  sizes: { width_m: number; height_m: number; count: number }[];
};

export type PreviewFatada = {
  objects: unknown[];
  cantitati: Record<string, Record<string, number>>;
  /** Lista de plăci pe corp (v. `RandPlaci`). */
  placi?: Record<string, RandPlaci[]>;
  stil: StilFatada;
  durata_s: number;
};

export type EroareStil = { error: string; erori?: string[] };

export const getFatada = (projectId: string) =>
  get<{ stil: StilFatada | null }>(`/api/projects/${projectId}/fatada`);
export const putFatada = (projectId: string, stil: StilFatada | null) =>
  put<{ stil: StilFatada | null }>(`/api/projects/${projectId}/fatada`, { stil });
/** Un rând din FIRUL RULĂRII agentului de fațadă (`pipeline.job_mesaj`):
 *  `tur` = propoziția pentru om a unui tur de model (poate lipsi), `pas` = un
 *  pas al agentului, cu cod de tradus, `final`, `eroare`. Gândirea brută a
 *  modelului NU vine niciodată aici (v. `control-plane/api/fatada.py`). */
export interface MesajAgent {
  seq: number;
  fel: "tur" | "pas" | "final" | "eroare" | string;
  text: string | null;
  text_cod: string | null;
  text_param: Record<string, string | number> | null;
  detaliu: Record<string, unknown>;
  created_at: string | null;
}
export interface FirAgent {
  job: { id: string; status: string; stage: string | null; stage_cod?: string | null;
         stage_param?: Record<string, string | number> | null;
         started_at: string | null; finished_at: string | null } | null;
  mesaje: MesajAgent[];
}
/** Firul ultimei rulări a agentului de fațadă pe o arhitectură, după `dupa`. */
export const getFirAgent = (projectId: string, solutionId: string, dupa = 0) =>
  get<FirAgent>(`/api/projects/${projectId}/solutions/${solutionId}/agent?dupa=${dupa}`);

/** Un articol al DEVIZULUI FAȚADEI (`cost_model.fatada`): un rând de metraj
 *  (capitol, element, articol, unitate, variantă) cu prețul lui și partea
 *  fiecărui corp. `articol` e cheia de cost; numele se traduce în ecran. */
export type ArticolFatada = {
  nr: number;
  capitol: string;
  capitol_cod: string;
  element: string;
  articol: string;
  um: "m2" | "ml" | "buc";
  cantitate: number;
  grosime_m?: number;
  inaltime_m?: number;
  vitraj?: string;
  deschidere?: string;
  corpuri: Record<string, number>;
  pu_material_eur: number;
  pu_manopera_eur: number;
  total_material_eur: number;
  total_manopera_eur: number;
  total_eur: number;
  sursa_pret: string;
  precizie: string;
  /** cheia rândului: pe ea se țin corecțiile omului */
  cheie: string;
  /** componentă (adeziv, dibluri, plasă…): cheia articolului ei și consumul
   *  pe unitatea lui */
  din?: string;
  consum?: number;
  /** ce a schimbat omul, cu valorile calculate lângă */
  modificat?: CampCorectie[];
  calculat?: Record<CampCorectie, number>;
};
export type CampCorectie = "cantitate" | "pu_material_eur" | "pu_manopera_eur";
/** Un rând fără preț: cantitatea lui, fără articol. */
export type RandFatada = Omit<ArticolFatada, "nr" | "capitol_cod" | "pu_material_eur"
  | "pu_manopera_eur" | "total_material_eur" | "total_manopera_eur" | "total_eur"
  | "sursa_pret" | "precizie" | "cheie">;
export type DevizFatada = {
  moneda: string;
  versiune_tarife: string;
  articole: ArticolFatada[];
  capitole: { cod: string; total_material_eur: number; total_manopera_eur: number;
              total_eur: number }[];
  /** se arată, nu se plătesc separat: plăci, rosturi, piesele substructurii */
  informative: RandFatada[];
  /** rânduri de metraj fără tarif */
  nepretuite: RandFatada[];
  corpuri: Record<string, { total_material_eur: number; total_manopera_eur: number;
                            total_direct_eur: number }>;
  total_material_eur: number;
  total_manopera_eur: number;
  total_direct_eur: number;
  indirecte: { cheie: string; procent: number; baza_eur: number; valoare_eur: number }[];
  total_eur: number;
  arie_fatada_m2: number | null;
  eur_pe_m2: number | null;
  precizie: "estimare" | "partial";
};
/** COSTUL ELEMENTELOR ALESE pe machetă, pe loc: obiectele din document ×
 *  tarifele fațadei (v. `viewer/fatada/costElement.ts`). Cost direct, fără
 *  sporuri; `indirecte` le dă separat. */
export type CostElemente = Pick<DevizFatada, "articole" | "informative" | "nepretuite"
  | "total_material_eur" | "total_manopera_eur" | "total_direct_eur" | "indirecte" | "total_eur"
  | "versiune_tarife" | "moneda">;
export const costElemente = (projectId: string, cerere: unknown) =>
  post<CostElemente>(`/api/projects/${projectId}/fatada/cost-elemente`, cerere);

/** DEVIZUL FAȚADEI unei soluții de arhitectură: metrajul tipizat × tarifele
 *  fațadei, socotit la citire. Gata de când fațada e — nu așteaptă structura. */
export const getDevizFatada = (projectId: string, solutionId: string) =>
  get<DevizFatada>(`/api/projects/${projectId}/solutions/${solutionId}/fatada/deviz`);
/** CORECȚIA OMULUI pe un rând al devizului fațadei: o valoare schimbă, `null`
 *  întoarce la cea calculată. Prețurile sunt ale proiectului, cantitatea a
 *  soluției. */
export const corecteazaDevizFatada = (projectId: string, solutionId: string, cheie: string,
                                      camp: CampCorectie, valoare: number | null) =>
  put<{ ok: boolean }>(`/api/projects/${projectId}/solutions/${solutionId}/fatada/corectii`,
                       { cheie, [camp]: valoare });

/** STRUCTURA FAȚADEI (`architecture/fatada_structura/`): vântul CR 1-1-4/2012
 *  pe fețele arhitecturii și dimensionarea peretelui cortină, socotite la
 *  citire. Forma e cea a raportului din `raport.calculeaza`; aici doar ce
 *  desenează pagina. */
export interface OptiuniStructuraFatada {
  teren?: string; q_b?: number | null; altitudine?: number | null;
  norma?: "np102" | "en13830"; schema?: "simpla" | "continua_2"; arie?: number; detaliu?: boolean;
}
export interface CelulaVant {
  u0: number; u1: number; arie_m2: number;
  w_pres: number; w_succ: number; zona_pres?: string | null; zona_succ?: string | null;
  caz_pres?: string | null; caz_succ?: string | null; w_pres_1m2: number; w_succ_1m2: number;
}
export interface NivelVant {
  level: number; z0: number; z1: number; h: number; z_sus_teren: number; celule: CelulaVant[];
  F_kn: Record<string, number>; F_pres_kn: number; F_succ_kn: number;
}
export interface FataVant {
  id: string; nume: string; lungime_m: number; a: number[]; b: number[]; n: number[]; rost: boolean;
  cortina: string[];
  directii: Record<string, { rol: string; e: number; b: number; d: number; h_pe_d: number; taieturi_u: number[] }>;
  niveluri: NivelVant[]; F_kn: Record<string, number>; F_pres_kn: number; F_succ_kn: number;
  w_pres_1m2_max: number; w_succ_1m2_max: number;
}
export interface MontantCalc {
  u: number; b_trib_m: number; L_m: number; w_k_kpa: number; zona?: string | null; caz?: string | null;
  q_k_kn_m: number; q_d_kn_m: number; sageata_adm_mm: number; Ix_nec_cm4: number; M_Ed_knm: number;
  Wx_nec_cm3: number; R_k_kn: number; R_d_kn: number; profil: string | null; adancime_mm?: number;
  Ix_cm4?: number; sageata_mm?: number; sigma_mpa?: number; f_d_mpa?: number; grad?: number; G_k_kn?: number;
  verdict: string;
}
export interface NivelCortina {
  level: number; L_m: number; n_montanti: number; w_k_max_kpa: number; q_k_max_kn_m: number;
  guvernant: MontantCalc | null; profil: string | null; R_d_max_kn: number; G_k_max_kn: number;
  traversa: { profil: string | null; adancime_mm?: number; L_m: number; Ix_nec_cm4: number; Iy_nec_cm4: number;
              sageata_h_mm?: number; sageata_v_mm?: number; verdict: string } | null;
  n_traverse: number; detaliu?: MontantCalc[];
}
/** Un sector de azimut al hărții orașului, cu ce e în el (fracții din
 *  suprafața penei, 0–1) și categoria de teren care iese (CR 1-1-4 Tabelul 2.1). */
export interface SectorTeren {
  k: number; nume: string; az0: number; az1: number; categorie: string;
  inalt: number; construit: number; construit_500: number; apa: number; verde: number; n_cladiri: number;
}
export interface TerenAuto {
  categorie: string; sectoare: SectorTeren[]; raza_m: number; raza_iii_m: number; h_inalt_m: number;
  n_cladiri: number; goala: boolean; complet: boolean;
  dale?: { total: number; gata: number; esuate: number } | null; sursa: string;
}
export interface StructuraFatada {
  versiune: number;
  amplasament: { adresa?: string | null; localitate?: string | null; judet?: string | null;
                 q_b_kpa: number; q_b_sursa: string; teren: string; teren_sursa: string; teren_descriere: string;
                 /** clasificarea hărții orașului, și când omul a ales altceva; `null` fără hartă */
                 teren_auto: TerenAuto | null;
                 /** adevărat când fiecare direcție a vântului are categoria sectorului ei */
                 teren_pe_directii: boolean;
                 z0_m: number; zmin_m: number; altitudine_m?: number | null; factor_altitudine: number;
                 q_b_calcul_kpa: number; c_o: number; avertismente: string[] };
  profil_q_p: Record<string, number[][]>;
  corpuri: {
    id: string; cod: string; h_m: number; ridicare_m: number; poly: number[][];
    niveluri: { level: number; z0: number; z1: number; h: number; q_p_kpa: number }[];
    are_cortina: boolean;
    /** ce a ales COMPUNEREA pe corp (profilul scris în document și ipotezele lui); `null` pe un document necompus cu dimensionare */
    compunere?: { profil: { montant?: string | null; montant_m?: number | null; traversa?: string | null; traversa_m?: number | null } | null;
                  dimensionare: { teren: string; teren_sursa: string; q_b_kpa: number; norma: string; w_k_max_kpa: number;
                                  guvernant?: { fata: string; nivel: number } | null; lipsa?: string[]; avertismente?: string[] } | null } | null;
    presiuni: { directii: { id: string; w: number[]; dinspre: string; unghi_deg: number; teren: string; d: number; b: number; h_pe_d: number; e: number }[];
                fete: FataVant[]; F_kn: Record<string, number>; avertismente: string[] };
    cortina: { norma_sageata: string; schema: string; catalog: { sistem: string; sursa: string; estimat: boolean };
               portiuni: { op: string; face: string; fata: string; u0: number; u1: number; modul_mediu_m: number;
                           niveluri: NivelCortina[] }[];
               profile_montanti: Record<string, number>; avertismente: string[] };
  }[];
  ipoteze: Record<string, unknown> & { nefacute?: string[]; norme?: string[] };
  avertismente: string[];
}
export const getStructuraFatada = (projectId: string, solutionId: string, o: OptiuniStructuraFatada = {}) => {
  const q = new URLSearchParams();
  if (o.teren) q.set("teren", o.teren);
  if (o.q_b != null) q.set("q_b", String(o.q_b));
  if (o.altitudine != null) q.set("altitudine", String(o.altitudine));
  if (o.norma) q.set("norma", o.norma);
  if (o.schema) q.set("schema", o.schema);
  if (o.arie != null) q.set("arie", String(o.arie));
  if (o.detaliu) q.set("detaliu", "1");
  const qs = q.toString();
  return get<StructuraFatada>(`/api/projects/${projectId}/solutions/${solutionId}/fatada/structura${qs ? `?${qs}` : ""}`);
};

export const getFatadaPresets = () =>
  get<Record<string, StilFatada>>(`/api/fatada/presets`);
export const previewFatada = (projectId: string, solutionId: string, stil: StilFatada | null) =>
  post<PreviewFatada>(`/api/projects/${projectId}/solutions/${solutionId}/fatada/preview`,
                      { stil });
/** FAȚADA APLICATĂ pe soluție: o soluție de arhitectură NOUĂ, cu aceleași etaje
 *  și stilul dat, derivată din cea dată. Secunde, nu minute — nu se reface
 *  compartimentarea, nici parcajul. Întoarce id-ul soluției noi. */
/** Rescrie PE LOC obiecte din documentul unei soluții arhitecturale — ce trimite
 *  editorul de nivel la «Salvează» (v. `viewer/plan/EditorNivel.tsx`). Obiectele
 *  sunt întregi și în coordonatele DOCUMENTULUI. */
export const scrieObiecteSolutie = (projectId: string, solutionId: string,
                                    obiecte: unknown[], sterge: string[]) =>
  post<{ ok: true; inlocuite: number; adaugate: number; sterse: number; dezlegate: number }>(
    `/api/projects/${projectId}/solutions/${solutionId}/document/obiecte`, { obiecte, sterge });

export const aplicaFatada = (projectId: string, solutionId: string, stil: StilFatada | null) =>
  post<{ solutie: string; cantitati: Record<string, Record<string, number>>; durata_s: number }>(
    `/api/projects/${projectId}/solutions/${solutionId}/fatada/aplica`, { stil });

/** Compoziția fațadei pe etajele scrise (secțiunea `architecture`), ÎN LOC pe
 *  soluție: raportul cu schimbările, refuzurile și măsurile pe fețe. */
export interface RaportCompozitie {
  ok: boolean; revizie: number;
  schimbari: Record<string, number>;
  refuzuri: { op: string; unde: string; motiv: string }[];
  inainte: Record<string, unknown>; dupa: Record<string, unknown>;
  durata_s?: number;
}
/** O variantă de recompartimentare a unui apartament (`pipeline.recomp_varianta`):
 *  camerele, pereții noi și ușile în coordonatele documentului, suprafețele și
 *  verificările — cât să se deseneze și să se compare. */
export interface VariantaRecomp {
  rang: number; tip: string; camere_n: number;
  util_m2: number; camere_m2: number; cost: number;
  camere: { id: string; fel: string; arie: number; poly: number[][] }[];
  /** conturul exact al apartamentului în varianta asta (camerele unite, fără spațiul
   *  administrativ al clădirii); lipsă pe rândurile scrise înainte să existe */
  contur?: number[][] | null;
  pereti: number[][][];
  usi: number[][][];
  /** ușa de intrare a variantei (segmentul ei, pe fața interioară a peretelui) */
  intrare?: number[][] | null;
  /** cât a mutat-o solverul și de unde (centrul ușii vechi) — numai dacă a mutat-o */
  intrare_mutata_m?: number;
  intrare_veche?: number[];
  verificari: { cheie: string; ok: boolean | string | null; text: string }[];
  praguri: { cheie: string; nume: string; val: number | null; catalog: number; ok: boolean | null }[];
}
export interface VarianteApartament {
  flat_id: string; cheie: { nivel: string; etaj?: number; label: string; centru: number[] };
  ales: number; oglinzi: string[]; variante: VariantaRecomp[]; la?: string | null;
}
/** Un nivel după recalcularea căilor de evacuare (v. `architecture/evacuare/recalcul.py`). */
export interface NivelEvacuareRecalculat {
  level: number; status: string; eroare?: string | null; iesiri: number; iesiri_negasite: string[];
  locuri: number; fara_ruta: number; cel_mai_lung_m: number | null; coridoare: number;
}
/** CĂILE DE EVACUARE REFĂCUTE PE IEȘIRILE EXISTENTE, în loc, după editarea
 *  parcajului: rutele și traseele pe planșa de acum, cu holurile neatinse. */
export const recalculeazaEvacuarea = (projectId: string, solutionId: string) =>
  post<{ ok: true; niveluri: NivelEvacuareRecalculat[];
         rezumat: { niveluri: number; fara_ruta: number; iesiri_negasite: number; picate: number };
         durata_s: number }>(
    `/api/projects/${projectId}/solutions/${solutionId}/evacuare/recalculeaza`, {});

/** Calea minimă de evacuare a unui loc, cerută abia la clic pe loc: stă în
 *  artefactul `evacuare_rute` al nivelului, nu în document (v.
 *  `control-plane/api/solutions.ruta_evacuare`). `null` = locul n-are rută sub
 *  prag, sau soluția e dinaintea artefactului. */
export const rutaEvacuarii = async (projectId: string, solutionId: string, storey: string, loc: string) => {
  try {
    return await get<{ iesire: string; distanta_m: number; puncte: number[][] }>(
      `/api/projects/${projectId}/solutions/${solutionId}/evacuare/ruta`
      + `?storey=${encodeURIComponent(storey)}&loc=${encodeURIComponent(loc)}`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
};

export const getRecompVariante = (projectId: string, solutionId: string, flatId?: string) =>
  get<{ apartamente: Record<string, VarianteApartament>; job: Job | null }>(
    `/api/projects/${projectId}/solutions/${solutionId}/recomp/variante`
    + (flatId ? `?flat=${encodeURIComponent(flatId)}` : ""));
/** Ce s-a schimbat în document la aplicarea unei variante (`layout.recomp.delta.jurnalul`):
 *  id-urile plecate, obiectele venite (în cadrul documentului, neorientate) și
 *  apartamentul cu conturul și cartea lui de vizită noi. */
export interface DeltaAplicata {
  flat_id: string; rang: number;
  sterge: string[];
  adauga: Record<string, unknown>[];
  flat: { geometry: Record<string, unknown>; props: Record<string, unknown>;
          display: Record<string, unknown> } | null;
}
/** Răspunsul DIRECT al aplicării: varianta e deja scrisă, din deltele ei, în secunde
 *  (`architecture/service.aplica_variante`). `reciteste` = fațada s-a re-îmbrăcat, deci
 *  documentul trebuie recitit, nu doar îmbinat. */
export interface AplicareDirecta {
  revizie: number; aplicate: DeltaAplicata[]; fatada_reimbracata: boolean;
  reciteste: boolean; durata_s: number;
}
export type AplicareRecomp = AplicareDirecta | Job;
export const eAplicareDirecta = (r: AplicareRecomp): r is AplicareDirecta =>
  Array.isArray((r as AplicareDirecta).aplicate);
/** «Aplică varianta»: direct (`AplicareDirecta`) când soluția are deltele variantelor,
 *  altfel jobul `recomp` care le calculează și scrie alegerea (soluție veche). */
export const aplicaRecompVarianta = (projectId: string, solutionId: string,
                                      flatId: string, rang: number, oglinzi: boolean) =>
  post<AplicareRecomp>(`/api/projects/${projectId}/solutions/${solutionId}/recomp/aplica`,
                       { flat_id: flatId, rang, oglinzi });

export const compuneArhitectura = (projectId: string, solutionId: string,
                                    arhitectura: Record<string, unknown>, rationale?: string,
                                    extra?: { fatada?: StilFatada | null; revizie_asteptata?: number }) =>
  post<RaportCompozitie>(
    `/api/projects/${projectId}/solutions/${solutionId}/arhitectura/compune`,
    { arhitectura, rationale, ...(extra ?? {}) });

/** STILUL SCRIS PE SOLUȚIA DESCHISĂ, în loc (Cătălin, 6.10.2026: schimbările
 *  rămân numai pe soluția pe care stă omul). `revizieAsteptata` = revizia pe
 *  care a văzut-o; 409 (`revizie_schimbata`) când altcineva a scris între timp. */
export const imbracaSolutia = (projectId: string, solutionId: string, stil: StilFatada | null,
                               revizieAsteptata?: number, rationale?: string) =>
  put<{ ok: true; revizie: number; cantitati: Record<string, Record<string, number>> }>(
    `/api/projects/${projectId}/solutions/${solutionId}/fatada`,
    { stil, rationale, ...(revizieAsteptata !== undefined ? { revizie_asteptata: revizieAsteptata } : {}) });
